import { useState } from "react";
import { AlertTriangle, Container, PackageCheck, Truck } from "lucide-react";
import { ArrivalDayDialog } from "@/components/overview/ArrivalDayDialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

/** Archive of shipments whose estimated time of arrival has passed without delivery. */
export function OverdueShipmentArchive({ projectId, data, onChanged, canConfirm = false }) {
  const [confirmDate, setConfirmDate] = useState(null);
  const groups = data?.groups || [];

  if (!groups.length)
    return (
      <p
        data-testid="overdue-archive-empty"
        className="mt-6 rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm text-slate-500"
      >
        No delayed shipments. Every shipped panel is either arrived or still within its estimated
        arrival window.
      </p>
    );

  return (
    <section className="mt-6" data-testid="overdue-archive-section">
      <div className="flex items-center gap-2">
        <AlertTriangle size={17} className="text-red-600" />
        <h2 className="font-display text-xl font-medium tracking-tight sm:text-2xl">
          Delayed Shipments
        </h2>
        <span className="mono rounded bg-red-500/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-red-700">
          {data.total_panels} panels
        </span>
      </div>

      <div className="mt-4 space-y-3">
        {groups.map((g) => (
          <div
            key={`${g.shipment_code || "none"}-${g.shipping_method || "none"}-${g.estimated_arrival}`}
            data-testid={`overdue-group-${g.shipment_code || "no-code"}-${g.estimated_arrival}`}
            className="rounded-xl border border-red-500/30 bg-white p-4"
          >
            <div className="flex flex-wrap items-center gap-2">
              {g.shipping_method === "By Container" ? (
                <Container size={14} className="okn-wave text-cyan-600" />
              ) : (
                <Truck size={14} className="text-cyan-600" />
              )}
              <span className="mono text-[12.5px] text-slate-900">
                {g.shipment_code || "No shipment code"}
              </span>
              <span className="mono text-[11px] text-slate-500">{g.shipping_method || "—"}</span>
              <span
                data-testid={`overdue-days-${g.shipment_code || "no-code"}-${g.estimated_arrival}`}
                className="mono ml-auto rounded-full bg-red-500/10 px-2.5 py-0.5 text-[10.5px] uppercase tracking-wider text-red-700"
              >
                {g.days_late} days late
              </span>
            </div>

            <div className="mt-3 grid gap-2 border-t border-slate-200 pt-2.5 text-[11.5px] sm:grid-cols-3">
              <div className="flex justify-between sm:block">
                <span className="text-slate-500">Shipped</span>
                <span className="mono block text-slate-800">{fmt(g.shipped_at)}</span>
              </div>
              <div className="flex justify-between sm:block">
                <span className="text-slate-500">Estimated time of arrival</span>
                <span className="mono block text-red-600">{fmt(g.estimated_arrival)}</span>
              </div>
              <div className="flex justify-between sm:block">
                <span className="text-slate-500">Panels waiting</span>
                <span className="mono block text-slate-800">{g.panel_count}</span>
              </div>
            </div>

            <div className="okn-scroll mt-3 max-h-28 overflow-y-auto">
              <div className="flex flex-wrap gap-1.5">
                {g.panels.map((p) => (
                  <span
                    key={p.panel_uid}
                    className="mono rounded border border-slate-200 bg-[#EEF2F8] px-1.5 py-0.5 text-[10px] text-slate-700"
                  >
                    {p.panel_name}
                    {p.pallet_code ? ` · ${p.pallet_code}` : ""}
                  </span>
                ))}
              </div>
            </div>

            {canConfirm ? (
              <button
                data-testid={`overdue-confirm-${g.shipment_code || "no-code"}-${g.estimated_arrival}`}
                onClick={() => setConfirmDate(g.estimated_arrival)}
                className="mono mt-3 flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-white px-3.5 py-1.5 text-[10.5px] uppercase tracking-wider text-emerald-700 hover:bg-emerald-50"
              >
                <PackageCheck size={13} /> Confirm delivery
              </button>
            ) : null}
          </div>
        ))}
      </div>

      <ArrivalDayDialog
        projectId={projectId}
        date={confirmDate}
        open={!!confirmDate}
        onOpenChange={(next) => !next && setConfirmDate(null)}
        onDone={onChanged}
      />
    </section>
  );
}
